import { useState, useRef, useEffect } from "react"

import { useSearchParams, useNavigate } from "react-router-dom"

import {
	Box,
	Typography,
	TextField,
	Link,
	Button,
} from "@mui/material"

import LandingFooter from "../components/LandingFooter"

import { useSnackbar } from "../components/SnackbarContext"
import { loader } from "../lib/loader"

import { getSessionInfo, verifyOtp } from "../services/otp.js"

import { formatTelephone } from "../utils"

import { isOtpRequestCooldownError, OTP_REQUEST_COOLDOWN_MESSAGE, requestOtp } from "../services/otp"
import { completeKwikLogin } from "../services/auth"

import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

dayjs.extend(utc)

const OtpInput = ({ onKeyDown, submit }) => {
	const [otp, setOtp] = useState(new Array(6).fill(""))
	const inputs = useRef([])

	const handleChange = (value, index) => {
		if (!/^\d?$/.test(value)) return

		const newOtp = [...otp]
		newOtp[index] = value
		setOtp(newOtp)

		if (value && index < 5)
			inputs.current[index + 1]?.focus()

		if (onKeyDown)
			onKeyDown(newOtp.join(""))
	}

	const handleKeyDown = (event, index) => {
		if (event.key === "Backspace") {
			if (otp[index]) {
				const newOtp = [...otp]
				newOtp[index] = ""
				setOtp(newOtp)
				if (onKeyDown) onKeyDown(newOtp.join(""))
			} else if (index > 0) {
				inputs.current[index - 1]?.focus()
			}
		} else if (event.key === "ArrowLeft" && index > 0) {
			inputs.current[index - 1]?.focus()
		} else if (event.key === "ArrowRight" && index < 5) {
			inputs.current[index + 1]?.focus()
		} else if (event.key === "Enter" && otp.join("").length === 6) {
			submit()
		}
	}

	return (
		<Box className="otp-input">
			{otp.map((digit, index) => (
				<TextField
					key={index}
					inputRef={(element) => (inputs.current[index] = element)}
					value={digit}
					onChange={(event) => handleChange(event.target.value, index)}
					onKeyDown={(event) => handleKeyDown(event, index)}
					autoFocus={index === 0}
					inputProps={{ maxLength: 1 }}
				/>
			))}
		</Box>
	)
}

const AuthenticationForm = ({ otp, setOtp, sessionInfo, login }) => {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
    const { showSnackbar } = useSnackbar()

	const [countdown, setCountdown] = useState("")

	useEffect(() => {
		if (! sessionInfo?.expires_at) 
			return

		const interval = setInterval(() => {
			const now = dayjs.utc()
			const expires = dayjs.utc(sessionInfo.expires_at)
			const diff = expires.diff(now, "second")

			if (diff <= 0) {
				setCountdown("00:00")
				clearInterval(interval)
			} else {
				const minutes = Math.floor(diff / 60).toString().padStart(2, "0")
				const seconds = (diff % 60).toString().padStart(2, "0")
				setCountdown(`${minutes}:${seconds}`)
			}
		}, 1000)

		return () => clearInterval(interval)
	}, [sessionInfo?.expires_at])

	const requestNewOtp = async () => {
		loader.on()
		try {
			const telephone = searchParams.get("telephone")
			const sessionId = searchParams.get("sessionId")
			const response = await requestOtp(telephone, sessionId)
			if (response.status === 200) {
				window.location.reload()
				return
			}
			if (isOtpRequestCooldownError(response)) {
				showSnackbar({
					message: OTP_REQUEST_COOLDOWN_MESSAGE,
					severity: "warning"
				})
				return
			}
			showSnackbar({
				message: "Não foi possível solicitar um novo código agora.",
				severity: "error"
			})
		} catch (err) {
			showSnackbar({
				message: "Algo deu errado, tente novamente em alguns instantes.",
				severity: "error"
			})
		} finally {
			loader.off()
		}
	}

	return (
		<Box className="container">
			<Typography variant="h4" mb={2}>
				Confirme seu acesso ao Kwik Scout
			</Typography>
			<Typography variant="body">
				Enviamos um código de 6 dígitos para o canal de autenticação configurado.
			</Typography>
			{sessionInfo?.telephone ? (
				<Typography variant="h6">
					{formatTelephone(sessionInfo.telephone)}
				</Typography>
			) : null}
			<OtpInput onKeyDown={(otp) => setOtp(otp)} submit={login} />
			{sessionInfo?.telephone ? (
				<Typography variant="subtitle2">
					{countdown !== "00:00"
						? <>Não recebeu o código? Você poderá solicitar outro código em {countdown || "05:00"}.</>
						: <>Não recebeu o código? <Link 
							sx={{ cursor: "pointer" }}
							onClick={() => requestNewOtp()}
						>Solicitar novo código</Link></>
					}
				</Typography>
			) : null}
			<Typography variant="subtitle2">
				Não é esse o seu número? <Link 
					sx={{ cursor: "pointer" }} 
					onClick={() => navigate("/")}
				>Alterar número</Link>
			</Typography>
			<Typography variant="body" my={3}>
				Caso não receba o código em até 2 minutos, verifique se o número está correto ou tente novamente mais tarde.
			</Typography>
			<Button 
				variant="contained" 
				disabled={otp.length !== 6}
				onClick={login}
			>
				Confirmar acesso
			</Button>
		</Box>
	)
}

const Authentication = () => {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
    const { showSnackbar } = useSnackbar()

	const [otp, setOtp] = useState("")
	const [sessionInfo, setSessionInfo] = useState({})
	const [invalidSession, setInvalidSession] = useState(false)

	useEffect(() => {
		const loadSession = async () => {
			const sessionId = searchParams.get("sessionId")
			if (! sessionId) {
				setInvalidSession(true)
				return
			}

			const sessionInfo = await getSessionInfo(sessionId)
			if (sessionInfo)
				setSessionInfo(sessionInfo)
		} 
		loadSession()
	}, [navigate, searchParams])

	const login = async () => {
		const sessionId = searchParams.get("sessionId")
		loader.on()
		try {
			const response = await verifyOtp(sessionId, otp)
			if (response.status === 200) {
				const data = await response.json()
				const loginResult = await completeKwikLogin(data)

				if (!loginResult.ok) {
					showSnackbar({
						message: loginResult.error,
						severity: "error",
					})
					return
				}

				navigate("/home")
				return
			}
			showSnackbar({
				message: "Código de verificação inválido ou expirado.",
				severity: "error"
			})
		} finally {
			loader.off()
		}
	}

	return (
		<Box className="landing">
			<Box className="landing-authentication-background">
				<Box className="landing-authentication">
					{! invalidSession 
						? <AuthenticationForm 
							otp={otp}
							setOtp={setOtp}
							sessionInfo={sessionInfo}
							login={login}
						/>
						: (
							<Box display="flex" flexDirection="column" gap={2}>
								<Typography variant="h6">
									Sessão inválida ou expirada.
								</Typography>
								<Box textAlign="center">
									<Button onClick={() => navigate("/")}>
										Retornar
									</Button>
								</Box>
							</Box>
						)
					}
				</Box>
			</Box>
			<LandingFooter />
		</Box>
	)
}

export default Authentication
